import { createSlice } from "@reduxjs/toolkit";
import { normalizeExamQuestionStatus } from "../../shared/utilities/helpers/examQuestionStatus";
import { getExamSectionKey } from "../../shared/utilities/helpers/examSectionKey";

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const getSelectedOptionFromQuestion = (question = {}) => {
  // Only hydrate from explicit "user selected" fields.
  // Backend may also include answer/correct keys; we must not treat those as user input.
  const directCandidates = [
    question.selectedOption,
    question.selected_option,
    question.selected_option_id,
    question.user_option_id,
    question.user_selected_option,
  ];

  for (const selected of directCandidates) {
    if (Array.isArray(selected)) return selected.length ? selected : null;
    if (hasValue(selected)) return selected;
  }

  // Some resume list payloads use `option_id` to carry user's chosen option.
  // Use this fallback only when backend indicates the question was attempted.
  const hasAttemptHint =
    Number(question?.question_status ?? question?.questionStatus) > 0 ||
    Number(question?.is_saved) === 1;
  if (hasAttemptHint && hasValue(question?.option_id)) {
    return question.option_id;
  }

  return null;
};

const getIntegerAnswerFromQuestion = (question = {}) => {
  // Only hydrate from explicit "user typed" fields.
  // Backend may include correct answer under other keys; ignore those.
  const integerValue =
    question.integerAnswer ??
    question.integer_Answer ??
    question.integer_answer ??
    question.option_int;
  if (integerValue === null || integerValue === undefined) return "";
  return String(integerValue).trim();
};

const getSelectedOptionFromSingleQuestion = (question = {}) => {
  const directSelected = question.selectedOption ?? question.selected_option;
  if (Array.isArray(directSelected)) {
    return directSelected.length ? directSelected : null;
  }
  if (hasValue(directSelected)) return directSelected;

  const userSelectedOptionIds = Array.isArray(question.option)
    ? question.option
        .filter((opt) => Number(opt?.is_user_correct_ans) === 1)
        .map((opt) => opt?.option_id)
        .filter(hasValue)
    : [];

  if (!userSelectedOptionIds.length) return null;
  return Number(question?.answer_type_id) === 2
    ? userSelectedOptionIds
    : userSelectedOptionIds[0];
};

const getIntegerAnswerFromSingleQuestion = (question = {}) => {
  const integerValue =
    question.integerAnswer ??
    question.integer_Answer ??
    question.integer_answer;
  if (integerValue === null || integerValue === undefined) return "";
  return String(integerValue).trim();
};

const hasAnsweredQuestion = (question = {}) => {
  const selected = question?.selectedOption;
  const hasSelectedOption = Array.isArray(selected)
    ? selected.length > 0
    : selected !== null &&
      selected !== undefined &&
      !(typeof selected === "string" && selected.trim() === "");

  const integerValue = question?.integerAnswer ?? question?.integer_Answer;
  const hasIntegerAnswer =
    integerValue !== null &&
    integerValue !== undefined &&
    String(integerValue).trim() !== "";

  return hasSelectedOption || hasIntegerAnswer;
};

const examSlice = createSlice({
  name: "exam",

  initialState: {
    timeLeft: null,

    examSubmitted: false,

    paper_duration: null,

    testType: null,

    testDetails: null,

    instructions: null,

    currentQuestionIndex: 0,

    question: null,

    questionsList: null,

    questionsData: [],

    sections: [],

    activeSection: null,

    loading: false,
    loadingTopics: false,

    error: null,

    currentLang: "en",

    practiceList: null,

    submitResult: null,
    bookmarks: [],
    testId: null,
    pyqYears: [],
    selectedPyqYear: null,
    pyqYearPaperList: [],
    plannerDetails: null,
    subjectList: [],
    selectedType: null,
    paperId: null,
    pyqTopicWiseSubjects: [],
    pyqSubjectWiseTopics: [],
    pyqTopicWiseSets: null,
    loadingTopicWiseSets: false,
    activeSubject: "",
    selectedTopic: null,
    selectedSetId: null,
    paperName: "",
    practiceMode: "test",
    practiceSummary: null,
    practiceStreak: null,

  },

  reducers: {
    // PYQ YEAR WISE
    setPaperId: (state, action) => {
      state.paperId = action.payload;
    },
    setPracticeList: (state, action) => {
      state.practiceList = action.payload;
    },
    setSelectedType: (state, action) => {
      state.selectedType = action.payload; // Update selected type
    },
    getPyqYearWiseRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    getPyqYearWiseSuccess: (state, action) => {
      state.loading = false;
      state.pyqYears = action.payload;
    },
    getPyqYearWiseFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    setSelectedPyqYear: (state, action) => {
      state.selectedPyqYear = action.payload;
    },
    // Topic-Wise Sets
    getPyqTopicWiseSetsRequest: (state) => {
      state.loadingTopicWiseSets = true;
      state.error = null;
    },
    // In your reducer
    getPyqTopicWiseSetsSuccess: (state, action) => {
      state.loadingTopicWiseSets = false;
      state.pyqTopicWiseSets = action.payload;
    },

    getPyqTopicWiseSetsFailure: (state, action) => {
      state.loadingTopicWiseSets = false;
      state.error = action.payload;
    },
    // PYQ TOPIC WISE
    getPyqTopicWiseRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    getPyqTopicWiseSuccess: (state, action) => {
      state.loading = false;
      state.pyqTopicWiseSubjects = action.payload;
    },
    getPyqTopicWiseFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // PYQ SUBJECT WISE
    getPyqSubjectWiseRequest: (state) => {
      state.loadingTopics = true;
      state.error = null;
      state.pyqSubjectWiseTopics = [];
    },
    getPyqSubjectWiseSuccess: (state, action) => {
      state.loadingTopics = false;
      state.pyqSubjectWiseTopics = action.payload;
    },
    getPyqSubjectWiseFailure: (state, action) => {
      state.loadingTopics = false;
      state.error = action.payload;
    },

    // PYQ YEARWISE PAPER LIST
    getPyqYearwisePapersRequest: (state) => {
      state.loading = true;
      state.error = null;
    },

    getPyqYearwisePapersSuccess: (state, action) => {
      state.loading = false;
      state.pyqYearPaperList = action.payload;
    },
//     getPyqYearwisePapersSuccess: (state, action) => {
//   state.loading = false;
//   state.pyqYearPaperList = action.payload?.data?.data || [];
// },


    getPyqYearwisePapersFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    setTimeLeft: (state, action) => {
      state.timeLeft = action.payload;
    },

    setTestType: (state, action) => {
      state.testType = action.payload;
    },

    setCurrentQuestionIndex: (state, action) => {
      state.currentQuestionIndex = action.payload;
    },

    // Instructions

    fetchExamInstructionRequest: (state) => {
      state.loading = true;

      state.error = null;
    },

    fetchExamInstructionSuccess: (state, action) => {
      state.loading = false;

      state.instructions = action.payload;
      state.paperId = action.payload?.paper_id;
        const mins = Number(action.payload?.duration_mins);
  state.paper_duration = mins ? mins * 60 : null;
    },

    fetchExamInstructionFailure: (state, action) => {
      state.loading = false;

      state.error = action.payload;
    },

    // Single Question

    fetchExamQuestionRequest: (state) => {
      state.loading = true;
      state.error = null;
      state.examSubmitted = false;
    },

    fetchExamQuestionSuccess: (state, action) => {
      state.question = action.payload;
      const isResumeFlow =
        state.questionsList?._isResume === true;
      const isFreshStart =
        !isResumeFlow ||
        state.testDetails?.forceFreshStart === true ||
        state.questionsList?._freshStart === true;

      const questionPayload = Array.isArray(action.payload)
        ? action.payload[0]
        : Array.isArray(action.payload?.data)
        ? action.payload.data[0]
        : action.payload?.data || action.payload;

      if (questionPayload?.qid !== undefined && questionPayload?.qid !== null) {
        const backendSelectedOption = isFreshStart
          ? null
          : getSelectedOptionFromSingleQuestion(questionPayload);
        const backendIntegerAnswer = isFreshStart
          ? ""
          : getIntegerAnswerFromSingleQuestion(questionPayload);

        state.questionsData = state.questionsData.map((q) => {
          if (String(q.qid) !== String(questionPayload.qid)) return q;

          const normalizedStatus = normalizeExamQuestionStatus({
            ...q,
            ...questionPayload,
            selectedOption: backendSelectedOption,
            integerAnswer: backendIntegerAnswer,
          });

          const mergedPayload = { ...questionPayload };
          if (state.questionsList?._hasNamedSections === false) {
            mergedPayload.section_name = q.section_name;
          }

          return {
            ...q,
            ...mergedPayload,
            selectedOption:
              backendSelectedOption !== null && backendSelectedOption !== undefined
                ? backendSelectedOption
                : q.selectedOption,
            integerAnswer:
              backendIntegerAnswer !== "" ? backendIntegerAnswer : q.integerAnswer,
            integer_Answer:
              backendIntegerAnswer !== "" ? backendIntegerAnswer : q.integer_Answer,
            marked: isFreshStart
              ? false
              : q.marked !== undefined
              ? q.marked
              : normalizedStatus === "marked" ||
                normalizedStatus === "answered_marked",
            status: isFreshStart
              ? q.status === "current"
                ? "current"
                : "unvisited"
              : q.status === "current"
              ? "current"
              : normalizedStatus,
          };
        });
      }

      state.loading = false;
    },

    fetchExamQuestionFailure: (state, action) => {
      state.loading = false;

      state.error = action.payload;
    },

    // All Questions

    fetchExamQuestionsListRequest: (state) => {
      state.loading = true;
      state.questionsList = null;
      // Clear stale single-question payload from previous attempts/routes.
      // Otherwise QuestionCard can mark an old qid as current before fresh fetch completes.
      state.question = null;
      // Clear previous run question map to avoid carrying answers/status
      // into a new adaptive/test session before fresh list arrives.
      state.questionsData = [];
      state.currentQuestionIndex = 0;
      state.activeSection = null;
      state.error = null;
    },

    fetchExamQuestionsListSuccess: (state, action) => {
      state.loading = false;
      const isResumeFlow = action.payload?._isResume === true;
      const isAdaptiveResumeFlow = action.payload?._isAdaptiveResume === true;
      const resumeSnapshotQuestions = Array.isArray(action.payload?._resumeSnapshot?.questions)
        ? action.payload._resumeSnapshot.questions
        : [];
      const resumeSnapshotMap = new Map(
        resumeSnapshotQuestions.map((q) => [String(q?.qid), q])
      );
      const isFreshStart =
        !isResumeFlow ||
        state.testDetails?.forceFreshStart === true ||
        action.payload?._freshStart === true;

      const listRows = action.payload?.data;
      const hasNamedSections =
        Array.isArray(listRows) &&
        listRows.some((row) => String(row.section_name ?? "").trim() !== "");

      state.questionsList = {
        ...action.payload,
        _hasNamedSections: hasNamedSections,
      };
      // state.paper_duration = action.payload.paper_duration;
      const previousQuestionsData = Array.isArray(state.questionsData)
        ? state.questionsData
        : [];
      state.questionsData = action.payload.data.map((q) => {
  // ✅ Check if snapshot already restored this question
  // const existingQ = state.questionsData?.find(
  //   (old) => String(old.qid) === String(q.qid)
  // );

  const existingQ = isResumeFlow
  ? resumeSnapshotMap.get(String(q.qid)) ??
    previousQuestionsData?.find((old) => String(old.qid) === String(q.qid))
  : null;

  const backendSelectedOption = isFreshStart ? null : getSelectedOptionFromQuestion(q);
  const backendIntegerAnswer = isFreshStart ? "" : getIntegerAnswerFromQuestion(q);
  const mergedSelectedOption =
    existingQ?.selectedOption !== undefined
      ? existingQ.selectedOption
      : backendSelectedOption;
  const mergedIntegerAnswer =
    existingQ?.integerAnswer !== undefined
      ? existingQ.integerAnswer
      : backendIntegerAnswer;
  const backendQuestionStatusHint = isFreshStart
    ? 0
    : Number(q?.question_status ?? q?.questionStatus) > 0
      ? Number(q?.question_status ?? q?.questionStatus)
      : Number(q?.is_saved) === 1
        ? 1
        : 0;
  const normalizedStatus = normalizeExamQuestionStatus({
    ...q,
    selectedOption: mergedSelectedOption,
    integerAnswer: mergedIntegerAnswer,
    marked: existingQ?.marked ?? false,
    question_status:
      existingQ?.question_status !== undefined
        ? existingQ.question_status
        : backendQuestionStatusHint,
    status: existingQ?.status ?? q?.status ?? q?.questionStatus,
  });

  return {
    qid: q.qid,
    ...q,

    // ✅ Preserve snapshot values
    selectedOption: mergedSelectedOption,
    integerAnswer: mergedIntegerAnswer,
    integer_Answer: mergedIntegerAnswer,

    integerChecked: false,
    answerChecked: false,
    showSolution: false,

    marked: existingQ?.marked ?? false,
    status: normalizedStatus,

    time: 0,
  };
});

      const rawLastQuestionId = isFreshStart
        ? 0
        : action.payload?.userGivenLastQuestionId;
      const hasLastQuestion =
        rawLastQuestionId !== null &&
        rawLastQuestionId !== undefined &&
        Number(rawLastQuestionId) !== 0;

      const matchesBackendLastQuestion = (q) =>
        String(q?.qid) === String(rawLastQuestionId) ||
        String(q?.id) === String(rawLastQuestionId) ||
        String(q?.question_id) === String(rawLastQuestionId) ||
        String(q?.questionId) === String(rawLastQuestionId);
      const resumeIndex = hasLastQuestion
        ? state.questionsData.findIndex(matchesBackendLastQuestion)
        : -1;
      const snapshotCurrentQid = action.payload?._resumeSnapshot?.currentQuestionQid;
      const snapshotResumeIndex =
        snapshotCurrentQid !== undefined && snapshotCurrentQid !== null
          ? state.questionsData.findIndex(
              (q) => String(q.qid) === String(snapshotCurrentQid)
            )
          : -1;
      const effectiveResumeIndex =
        resumeIndex >= 0
          ? resumeIndex
          : snapshotResumeIndex >= 0
            ? snapshotResumeIndex
            : 0;
      const adaptiveAttemptFallbackIndex = isAdaptiveResumeFlow
        ? state.questionsData.findIndex((question) => {
            const selected = question?.selectedOption;
            const hasSelectedOption = Array.isArray(selected)
              ? selected.length > 0
              : selected !== null &&
                selected !== undefined &&
                !(typeof selected === "string" && selected === "");
            const hasIntegerAnswer =
              question?.integerAnswer !== null &&
              question?.integerAnswer !== undefined &&
              String(question?.integerAnswer ?? "").trim() !== "";
            const hasSavedAttempt =
              Number(question?.is_saved) === 1 ||
              Number(question?.question_status ?? question?.questionStatus) > 0;
            return !(
              hasSelectedOption ||
              hasIntegerAnswer ||
              hasSavedAttempt
            );
          })
        : -1;
      const adaptiveEffectiveResumeIndex =
        isAdaptiveResumeFlow && effectiveResumeIndex === 0 && adaptiveAttemptFallbackIndex >= 0
          ? adaptiveAttemptFallbackIndex
          : effectiveResumeIndex;

      // state.currentQuestionIndex = resumeIndex >= 0 ? resumeIndex : 0;

      // For both adaptive and non-adaptive resume, honor backend last-question pointer.
      // Snapshot restore (if present) can still refine this afterward.
      state.currentQuestionIndex = adaptiveEffectiveResumeIndex;
      if (state.questionsData[state.currentQuestionIndex]) {
        state.questionsData[state.currentQuestionIndex].status = "current";
      }

      state.sections = [
        ...new Set(action.payload.data.map((q) => getExamSectionKey(q))),
      ];

      const activeQuestion = state.questionsData[state.currentQuestionIndex];
      state.activeSection = activeQuestion
        ? getExamSectionKey(activeQuestion)
        : state.sections[0] || null;
    },
    applyResumeSnapshot: (state, action) => {
      const payload = action.payload || {};
      const snapshot = payload?.snapshot ?? payload;
      const skipCursorRestore = payload?.skipCursorRestore === true;
      const snapshotQuestions = Array.isArray(snapshot.questions)
        ? snapshot.questions
        : [];

      if (!snapshotQuestions.length || !state.questionsData.length) return;

      const snapshotMap = new Map(
        snapshotQuestions.map((q) => [String(q.qid), q])
      );

      state.questionsData = state.questionsData.map((question) => {
        const saved = snapshotMap.get(String(question.qid));
        if (!saved) return question;

        const merged = {
          ...question,
          selectedOption:
            saved.selectedOption !== undefined
              ? saved.selectedOption
              : question.selectedOption,
          integerAnswer:
            saved.integerAnswer !== undefined
              ? saved.integerAnswer
              : question.integerAnswer,
          marked: saved.marked !== undefined ? saved.marked : question.marked,
          question_status:
            saved.question_status !== undefined
              ? saved.question_status
              : question.question_status,
        };

        return {
          ...merged,
          status: normalizeExamQuestionStatus({
            ...merged,
            status: saved.status ?? merged.status,
          }),
        };
      });

      if (
        !skipCursorRestore &&
        snapshot.currentQuestionQid !== undefined &&
        snapshot.currentQuestionQid !== null
      ) {
        const resumeIndex = state.questionsData.findIndex(
          (q) => String(q.qid) === String(snapshot.currentQuestionQid)
        );

        if (resumeIndex >= 0) {
          state.questionsData = state.questionsData.map((q, idx) => {
            if (idx === resumeIndex) return { ...q, status: "current" };
            if (q.status === "current") {
              return {
                ...q,
                status: normalizeExamQuestionStatus(q),
              };
            }
            return q;
          });

          state.currentQuestionIndex = resumeIndex;
          const current = state.questionsData[resumeIndex];
          if (current) {
            state.activeSection = getExamSectionKey(current);
          }
        }
      }
    },

    fetchExamQuestionsListFailure: (state, action) => {
      state.loading = false;

      state.error = action.payload;
    },
    updateQuestionTime: (state, action) => {
      const { index } = action.payload;
      if (state.questionsData[index]) {
        state.questionsData[index].time += 1;
      }
    },
    resetTimer: (state, action) => {
      state.timeLeft = action.payload;
      state.questionsData = state.questionsData.map((q) => ({
        ...q,
        time: 0,
      }));
    },

    //     updateQuestionStatus: (state, action) => {
    //       const { qid, selectedOption,integerAnswer, makeCurrent, moveAway, markReview } =
    //         action.payload;

    //       const q = state.questionsData.find((q) => q.qid === qid);
    //       if (!q) return;

    //       if (makeCurrent) {
    //         // Before making this one current, finalize any previous current
    //         state.questionsData.forEach((ques) => {
    //           if (ques.status === "current") {
    //             if (ques.marked) {
    //               ques.status = ques.selectedOption ? "answered_marked" : "marked";
    //             } else {
    //               ques.status = ques.selectedOption ? "answered" : "unanswered";
    //             }
    //           }
    //         });

    //         q.status = "current";
    //         return;
    //       }

    //       if (selectedOption !== undefined) {
    //         q.selectedOption =
    //           q.selectedOption === selectedOption ? null : selectedOption;

    //         if (q.status !== "current") {
    //           if (q.marked) {
    //             q.status = q.selectedOption ? "answered_marked" : "marked";
    //           } else {
    //             q.status = q.selectedOption ? "answered" : "unanswered";
    //           }
    //         }
    //       }

    //       if (markReview !== undefined) {
    //   q.marked = markReview;
    //   if (q.status !== "current") {
    //     if (q.marked) {
    //       q.status = q.selectedOption ? "answered_marked" : "marked";
    //     } else {
    //       q.status = q.selectedOption ? "answered" : "unanswered";
    //     }
    //   }
    // }

    //       if (moveAway) {
    //         // finalize status when leaving
    //         if (q.marked) {
    //           q.status = q.selectedOption ? "answered_marked" : "marked";
    //         } else {
    //           q.status = q.selectedOption ? "answered" : "unanswered";
    //         }
    //       }
    //     },

    updateQuestionStatus: (state, action) => {
      const {
        qid,
        selectedOption,
        integerAnswer,
        integerChecked,
        answerChecked,
        showSolution,
        makeCurrent,
        moveAway,
        markReview,
      } = action.payload;

      const q = state.questionsData.find((q) => q.qid === qid);
      if (!q) return;

      if (makeCurrent) {
        state.questionsData.forEach((ques) => {
          if (ques.status === "current") {
            const hasAnswer = hasAnsweredQuestion(ques);

            if (ques.marked) {
              ques.status = hasAnswer ? "answered_marked" : "marked";
            } else {
              ques.status = hasAnswer ? "answered" : "unanswered";
            }
          }
        });

        q.status = "current";
        return;
      }

      /* ---------------- MCQ ANSWER ---------------- */
      if (selectedOption !== undefined) {
        q.selectedOption =
          q.selectedOption === selectedOption ? null : selectedOption;
        q.answerChecked = false;
        q.showSolution = false;
      }

      /* ---------------- INTEGER ANSWER ---------------- */
      if (integerAnswer !== undefined) {
        q.integerAnswer = integerAnswer;
        q.answerChecked = false;
        q.showSolution = false;
      }

      if (integerChecked !== undefined) {
        q.integerChecked = integerChecked;
      }

      if (answerChecked !== undefined) {
        q.answerChecked = answerChecked;
      }

      if (showSolution !== undefined) {
        q.showSolution = showSolution;
      }

      /* ---------------- MARK FOR REVIEW ---------------- */
      if (markReview !== undefined) {
        q.marked = markReview;
      }

      /* ---------------- STATUS UPDATE ---------------- */
      if (q.status !== "current") {
        const hasAnswer = hasAnsweredQuestion(q);

        if (q.marked) {
          q.status = hasAnswer ? "answered_marked" : "marked";
        } else {
          q.status = hasAnswer ? "answered" : "unanswered";
        }
      }

      /* ---------------- MOVE AWAY ---------------- */
      if (moveAway) {
        const hasAnswer = hasAnsweredQuestion(q);

        if (q.marked) {
          q.status = hasAnswer ? "answered_marked" : "marked";
          q.question_status = hasAnswer ? 4 : 3;
        } else {
          q.status = hasAnswer ? "answered" : "unanswered";
          q.question_status = hasAnswer ? 1 : 2;
        }
        q.is_saved = hasAnswer ? 1 : 0;
      }
    },

    markQuestionForReview: (state, action) => {
      const { qid } = action.payload;
      const question = state.questionsData.find((q) => q.qid === qid);

      if (question) {
        if (question.selectedOption) {
          question.status = "answered_marked"; // selected + marked
        } else {
          question.status = "marked"; // just marked
        }
      }
    },

    // Section change
    setActiveSection: (state, action) => {
      state.activeSection = action.payload;
    },

    // Save Answer
    // In gamification instant-feedback we pass `skipAdvance: true`.
    // In that case we should NOT block the whole UI with `loading` spinner.
    saveAnswerRequest: (state, action) => {
      if (action.payload?.skipAdvance === true) {
        return;
      }
      state.loading = true;
      state.error = null;
    },

    saveAnswerSuccess: (state) => {
      state.loading = false;
    },

    saveAnswerFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    setPracticeStreak: (state, action) => {
  state.practiceStreak = action.payload;
},
resetPracticeStreak: (state) => {
  state.practiceStreak = null;
},

    hidePracticeProgressiveMessage: (state) => {
      if (!state.practiceStreak) return;
      state.practiceStreak = {
        ...state.practiceStreak,
        progressive_message: null,
      };
    },



    // Bookmark
    saveBookmarkRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    saveBookmarkSuccess: (state, action) => {
      state.loading = false;
      state.bookmarks.push(action.payload);
    },
    saveBookmarkFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Language
    setLanguage: (state, action) => {
      state.currentLang = action.payload;
    },
    fetchPracticeListRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchPracticeListSuccess: (state, action) => {
      state.loading = false;
      state.practiceList = action.payload;
    },
    fetchPracticeListFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    submitExamRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    submitExamSuccess: (state, action) => {
      state.loading = false;
      state.submitResult = action.payload;
      state.examSubmitted = true;

      // ✅ DO NOT RESET testId / testDetails
      // Analytics depends on these

      state.questionsData = state.questionsData.map((q) => ({
        ...q,
        time: 0,
      }));
    },

    submitExamFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    resetSubmitResult: (state) => {
      state.submitResult = null;
    },

    setTestId: (state, action) => {
      state.testId = action.payload;
    },
    setTestDetails: (state, action) => {
      state.testDetails = action.payload;
      state.testId = action.payload?.testId;
      state.practiceMode = action.payload?.practiceMode;

      // Reattempt / new Start: drop persisted question progress so we don't
      // rebuild a local resume snapshot from the previous attempt.
      if (action.payload?.forceFreshStart === true) {
        state.questionsData = [];
        state.question = null;
        state.questionsList = null;
        state.currentQuestionIndex = 0;
        state.activeSection = null;
        state.examSubmitted = false;
        state.submitResult = null;
      }
    },
    setPlannerDetails: (state, action) => {
      state.plannerDetails = action.payload;
    },

    setActiveSubject: (state, action) => {
      state.activeSubject = action.payload;
    },

    // Action to set selected topic
    setSelectedTopic: (state, action) => {
      state.selectedTopic = action.payload;
    },

    //subject list
    fetchSubjectListRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
  fetchSubjectListSuccess: (state, action) => {
  state.loading = false;
  state.subjectList = action.payload.data.subject;
},
    fetchSubjectListFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload; // Set loading flag
    },
    setSelectedSetId: (state, action) => {
      state.selectedSetId = action.payload;
    },
    setPaperName: (state, action) => {
      state.paperName = action.payload;
    },
    fetchPracticeSummaryRequest: (state) => {
      state.loading = true;
      state.error = null;
    },

    fetchPracticeSummarySuccess: (state, action) => {
      state.loading = false;
      state.practiceSummary = action.payload;
    },

    fetchPracticeSummaryFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  setTimeLeft,
  resetSubmitResult,
  setPaperName,
  updateQuestionTime,
  resetTimer,
  setTestType,
  setTestDetails,
  setCurrentQuestionIndex,

  fetchExamInstructionRequest,

  fetchExamInstructionSuccess,

  fetchExamInstructionFailure,

  fetchExamQuestionRequest,

  fetchExamQuestionSuccess,

  fetchExamQuestionFailure,

  fetchExamQuestionsListRequest,

  fetchExamQuestionsListSuccess,

  fetchExamQuestionsListFailure,
  applyResumeSnapshot,

  updateQuestionStatus,

  setActiveSection,

  saveAnswerRequest,

  saveAnswerSuccess,

  saveAnswerFailure,

  setLanguage,

  fetchPracticeListRequest,
  fetchPracticeListSuccess,

  fetchPracticeListFailure,

  submitExamRequest,

  submitExamSuccess,

  submitExamFailure,

  saveBookmarkRequest,

  saveBookmarkSuccess,

  saveBookmarkFailure,

  markQuestionForReview,
  setTestId,
  getPyqYearWiseRequest,
  getPyqYearWiseSuccess,
  getPyqYearWiseFailure,
  setSelectedPyqYear,
  getPyqYearwisePapersRequest,
  getPyqYearwisePapersSuccess,
  getPyqYearwisePapersFailure,
  setPlannerDetails,
  fetchSubjectListRequest,
  fetchSubjectListSuccess,
  fetchSubjectListFailure,
  setPracticeList,
  setSelectedType,
  setPaperId,
  setLoading,
  getPyqTopicWiseRequest,
  getPyqTopicWiseSuccess,
  getPyqTopicWiseFailure,
  getPyqSubjectWiseRequest,
  getPyqSubjectWiseSuccess,
  getPyqSubjectWiseFailure,
  getPyqTopicWiseSetsRequest,
  getPyqTopicWiseSetsSuccess,
  getPyqTopicWiseSetsFailure,
  setActiveSubject,
  setSelectedTopic,
  setSelectedSetId,
  fetchBasicAnalyticsRequest,
  fetchPracticeSummaryRequest,
  fetchPracticeSummarySuccess,
  fetchPracticeSummaryFailure,
  setPracticeStreak,
   resetPracticeStreak,
  hidePracticeProgressiveMessage,
} = examSlice.actions;

export default examSlice.reducer;
